import { Component, type CSSProperties, type ErrorInfo, type PropsWithChildren } from "react";

import { captureRootRenderCrash } from "@/qwib-telemetry";

/**
 * The outermost boundary in QWIB, above auth, Apollo, LaunchDarkly, the theme and the router.
 *
 * React unmounts the whole tree when a render throws with nothing to catch it, which on this app
 * leaves an empty `#root` — the featureless gray viewport B-2012 reported, with no way out but a
 * manual reload the user has no reason to know will help.
 *
 * Everything below is deliberately dependency-free: no `@qw/ui`, no router, no theme, no icons,
 * inline styles only. A fallback that needs a provider is a fallback that cannot render the one time
 * it is needed — including when the provider itself is what threw.
 */

const reload = () => window.location.reload();

const styles = {
  // Light-only on purpose: the theme class lives on a provider that is, by construction, gone.
  // Fixed neutrals render the same either way rather than inheriting a half-torn-down theme.
  viewport: {
    alignItems: "center",
    backgroundColor: "#f4f4f5",
    color: "#18181b",
    display: "flex",
    fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
    inset: 0,
    justifyContent: "center",
    padding: "24px",
    position: "fixed",
  },
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e4e4e7",
    borderRadius: "12px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
    maxWidth: "420px",
    padding: "32px",
    textAlign: "center",
  },
  title: { fontSize: "18px", fontWeight: 600, margin: "0 0 8px" },
  body: { color: "#52525b", fontSize: "14px", lineHeight: 1.5, margin: "0 0 24px" },
  button: {
    backgroundColor: "#18181b",
    border: "none",
    borderRadius: "8px",
    color: "#ffffff",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "14px",
    fontWeight: 500,
    padding: "10px 20px",
  },
} as const satisfies Record<string, CSSProperties>;

export class RootErrorBoundary extends Component<PropsWithChildren, { hasError: boolean }> {
  state = { hasError: false };

  /**
   * One event per crash. The boundary has no reset path — the fallback's only action is a reload —
   * so a single flag is all the de-duplication a mounted instance can need.
   */
  private hasReported = false;

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    if (this.hasReported) {
      return;
    }

    this.hasReported = true;

    // The fallback is the last thing standing, so nothing it depends on may throw. `capture()`
    // enforces the package's PII contract by throwing, and reading a thrown value's `name` runs
    // whatever getter the throw site defined — either would escape to no boundary at all and put
    // the empty viewport back.
    try {
      captureRootRenderCrash(error, errorInfo.componentStack);
    } catch {
      // Losing the event is the acceptable outcome here; losing the fallback is not.
    }
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={styles.viewport} role="alert">
        <div style={styles.card}>
          <h1 style={styles.title}>QWIB hit an unexpected error</h1>
          <p style={styles.body}>
            The page stopped responding and could not recover on its own. Reloading usually brings it back — your
            messages and drafts are saved on the server.
          </p>
          <button type="button" style={styles.button} onClick={reload}>
            Reload QWIB
          </button>
        </div>
      </div>
    );
  }
}
